(function () {
  const DURATION = 200;
  const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

  class VariantMediaController {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.section;
      this.cleanupTimers = new Set();
      this.animationClones = new Set();
      this.collect();
    }

    destroy() {
      this.cleanupTimers.forEach((timer) => window.clearTimeout(timer));
      this.cleanupTimers.clear();
      this.animationClones.forEach((clone) => clone.remove());
      this.animationClones.clear();
      this.clearInlineAnimationStyles();
    }

    refresh(options = {}) {
      const variant = this.getSelectedVariant();
      this.applyVariant({
        variantId: variant?.id ?? null,
        featuredMediaId: variant?.featured_media?.id ?? null,
        animate: options.animate === true,
      });
    }

    applyVariant({ variantId = null, featuredMediaId = null, animate = true } = {}) {
      this.collect();
      this.clearRunningAnimations();

      const resolvedVariantId = variantId != null ? String(variantId) : this.getSelectedVariantId();
      const resolvedFeaturedMediaId = featuredMediaId != null ? String(featuredMediaId) : this.getSelectedFeaturedMediaId();
      const visibleIds = this.getVisibleProductMediaIds(resolvedVariantId);
      const nextActiveProductMediaId = this.resolveNextActiveProductMediaId({
        visibleIds,
        featuredMediaId: resolvedFeaturedMediaId,
      });

      this.applyViewerSurface({
        visibleIds,
        activeProductMediaId: nextActiveProductMediaId,
        featuredMediaId: resolvedFeaturedMediaId,
        animate,
      });
      this.applyFlipSurface(this.thumbnailItems, visibleIds, {
        animate,
        container: this.thumbnailList,
        featuredMediaId: resolvedFeaturedMediaId,
      });
      this.applyFlipSurface(this.stackItems, visibleIds, {
        animate,
        container: this.stackContainer,
        featuredMediaId: resolvedFeaturedMediaId,
      });
      this.applyLayeredSurface({
        visibleIds,
        activeProductMediaId: nextActiveProductMediaId,
        featuredMediaId: resolvedFeaturedMediaId,
        animate,
      });
      this.applyModalSurface(visibleIds, animate && this.isModalOpen(), resolvedFeaturedMediaId);

      this.syncViewerActive(nextActiveProductMediaId, resolvedFeaturedMediaId);
      this.syncStackActive(nextActiveProductMediaId);
      this.syncModalActive({
        visibleIds,
        activeProductMediaId: nextActiveProductMediaId,
      });
      this.refreshInteractiveComponents();
    }

    collect() {
      this.mediaGallery = this.root.querySelector('media-gallery');
      this.productGallery = this.root.querySelector('product-gallery');
      this.viewerList = this.productGallery?.querySelector('ul') || null;
      this.thumbnailSlider = this.root.querySelector('thumbnail-slider');
      this.thumbnailList = this.thumbnailSlider?.querySelector('.thumbnail-list') || null;
      this.stackContainer = this.root.querySelector('product-thumbnails') || null;
      this.layeredContainer = this.root.querySelector('.images-with-text__images') || null;

      this.viewerItems = Array.from(
        this.root.querySelectorAll('product-gallery .product__media-item[data-product-media-id]')
      );
      this.thumbnailItems = Array.from(
        this.root.querySelectorAll('thumbnail-slider .thumbnail-list__item[data-product-media-id]')
      );
      this.stackItems = Array.from(
        this.root.querySelectorAll('product-thumbnails .product__thumbnail[data-product-media-id]')
      );
      this.layeredItems = Array.from(
        this.root.querySelectorAll('.images-with-text__image[data-product-media-id]')
      );

      this.productModal = document.querySelector(`#ProductModal-${this.sectionId}`);
      this.modalContent = this.productModal?.querySelector('.product-media-modal__content') || null;
      this.modalItems = Array.from(this.modalContent?.querySelectorAll('[data-product-media-id]') || []);

      this.annotateBaseOrder(this.viewerItems);
      this.annotateBaseOrder(this.thumbnailItems);
      this.annotateBaseOrder(this.stackItems);
      this.annotateBaseOrder(this.layeredItems);
      this.annotateBaseOrder(this.modalItems);

      this.mediaGroups = new Map();
      [
        ...this.viewerItems,
        ...this.thumbnailItems,
        ...this.stackItems,
        ...this.layeredItems,
        ...this.modalItems,
      ].forEach((node) => {
        const productMediaId = this.getProductMediaId(node);
        if (!productMediaId) return;
        if (!this.mediaGroups.has(productMediaId)) {
          this.mediaGroups.set(productMediaId, {
            productMediaId,
            scope: node.dataset.mediaScope || 'general',
            variantId: node.dataset.mediaVariantId || '',
            nodes: [],
          });
        }
        this.mediaGroups.get(productMediaId).nodes.push(node);
      });
    }

    annotateBaseOrder(items) {
      items.forEach((item, index) => {
        if (!item.dataset.variantMediaBaseOrder) {
          item.dataset.variantMediaBaseOrder = String(index);
        }
      });
    }

    getSelectedVariant() {
      const selectedVariantNode = this.root.querySelector('variant-selects [data-selected-variant]');
      if (!selectedVariantNode) return null;

      try {
        return JSON.parse(selectedVariantNode.textContent);
      } catch (_error) {
        return null;
      }
    }

    getSelectedVariantId() {
      const variant = this.getSelectedVariant();
      return variant?.id != null ? String(variant.id) : '';
    }

    getSelectedFeaturedMediaId() {
      const variant = this.getSelectedVariant();
      return variant?.featured_media?.id != null ? String(variant.featured_media.id) : '';
    }

    prefersReducedMotion() {
      try {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      } catch (_error) {
        return false;
      }
    }

    isModalOpen() {
      return !!this.productModal?.hasAttribute('open');
    }

    getProductMediaId(node) {
      return String(node?.dataset?.productMediaId || '').trim();
    }

    isNodeVisible(node) {
      return !!node && node.dataset.mediaVisible !== 'false' && !node.hidden;
    }

    setNodeVisible(node, visible) {
      if (!node) return;
      node.dataset.mediaVisible = visible ? 'true' : 'false';
      if (visible) {
        node.hidden = false;
        node.removeAttribute('hidden');
      } else {
        node.hidden = true;
        node.setAttribute('hidden', '');
      }
    }

    hydrateNode(node) {
      if (!node) return;
      node.querySelectorAll('img[data-deferred="true"]').forEach((image) => {
        if (image.dataset.variantMediaHydrated === 'true') return;
        if (image.dataset.src) image.setAttribute('src', image.dataset.src);
        if (image.dataset.srcset) image.setAttribute('srcset', image.dataset.srcset);
        image.dataset.variantMediaHydrated = 'true';
      });
    }

    hydrateNodes(nodes) {
      nodes.forEach((node) => this.hydrateNode(node));
    }

    getVisibleProductMediaIds(variantId) {
      const currentVariantId = String(variantId || '').trim();
      const visibleIds = new Set();

      this.mediaGroups.forEach((group) => {
        if (group.scope !== 'variant') {
          visibleIds.add(group.productMediaId);
          return;
        }

        if (group.variantId && group.variantId === currentVariantId) {
          visibleIds.add(group.productMediaId);
        }
      });

      return visibleIds;
    }

    getCurrentActiveProductMediaId() {
      const activeViewerItem = this.viewerItems.find(
        (item) => item.classList.contains('is-active') && this.isNodeVisible(item)
      ) || this.viewerItems.find((item) => item.classList.contains('is-active'));
      if (activeViewerItem) return this.getProductMediaId(activeViewerItem);

      const activeLayeredItem = this.layeredItems.find(
        (item) => item.classList.contains('is-active') && this.isNodeVisible(item)
      ) || this.layeredItems.find((item) => item.classList.contains('is-active'));
      if (activeLayeredItem) return this.getProductMediaId(activeLayeredItem);

      return '';
    }

    resolveNextActiveProductMediaId({ visibleIds, featuredMediaId }) {
      const featuredId = String(featuredMediaId || '').trim();
      if (featuredId && visibleIds.has(featuredId)) {
        return featuredId;
      }

      const currentActiveProductMediaId = this.getCurrentActiveProductMediaId();
      if (currentActiveProductMediaId && visibleIds.has(currentActiveProductMediaId)) {
        return currentActiveProductMediaId;
      }

      const orderedNodes = this.viewerItems.length
        ? this.viewerItems
        : (this.layeredItems.length ? this.layeredItems : this.modalItems);
      const firstVisibleNode = orderedNodes.find((node) => visibleIds.has(this.getProductMediaId(node)));
      return firstVisibleNode ? this.getProductMediaId(firstVisibleNode) : '';
    }

    reorderSurface(items, container, { visibleIds, featuredMediaId } = {}) {
      if (!items.length || !container) return items;

      const featuredId = String(featuredMediaId || '').trim();
      const orderedItems = items.slice().sort((left, right) => {
        const leftId = this.getProductMediaId(left);
        const rightId = this.getProductMediaId(right);
        const leftVisible = visibleIds.has(leftId);
        const rightVisible = visibleIds.has(rightId);
        const leftIsFeatured = !!featuredId && leftVisible && leftId === featuredId;
        const rightIsFeatured = !!featuredId && rightVisible && rightId === featuredId;

        const leftBucket = leftIsFeatured ? 0 : leftVisible ? 1 : 2;
        const rightBucket = rightIsFeatured ? 0 : rightVisible ? 1 : 2;
        if (leftBucket !== rightBucket) return leftBucket - rightBucket;

        return Number(left.dataset.variantMediaBaseOrder || 0) - Number(right.dataset.variantMediaBaseOrder || 0);
      });

      orderedItems.forEach((item) => {
        if (item.parentElement === container) {
          container.appendChild(item);
        }
      });
      return orderedItems;
    }

    clearRunningAnimations() {
      this.cleanupTimers.forEach((timer) => window.clearTimeout(timer));
      this.cleanupTimers.clear();
      this.animationClones.forEach((clone) => clone.remove());
      this.animationClones.clear();
      this.clearInlineAnimationStyles();
    }

    clearInlineAnimationStyles() {
      [
        ...this.viewerItems,
        ...this.thumbnailItems,
        ...this.stackItems,
        ...this.layeredItems,
        ...this.modalItems,
      ].forEach((node) => {
        node.style.transition = '';
        node.style.transform = '';
        node.style.opacity = '';
        node.style.willChange = '';
        node.querySelectorAll('.product__media, .media-wrapper, .media').forEach((child) => {
          child.style.transition = '';
          child.style.transform = '';
          child.style.opacity = '';
          child.style.willChange = '';
        });
      });

      this.productGallery?.style.removeProperty('--variant-media-transitioning');
    }

    createLeavingClone(node) {
      const rect = node.getBoundingClientRect();
      if (!rect.width || !rect.height) return null;

      const clone = node.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.classList.add('variant-media-clone');
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.style.position = 'fixed';
      clone.style.left = `${rect.left}px`;
      clone.style.top = `${rect.top}px`;
      clone.style.width = `${rect.width}px`;
      clone.style.height = `${rect.height}px`;
      clone.style.margin = '0';
      clone.style.pointerEvents = 'none';
      clone.style.zIndex = '50';
      clone.style.transformOrigin = 'center center';
      clone.style.transition = `opacity ${DURATION}ms ${EASING}, transform ${DURATION}ms ${EASING}`;
      document.body.appendChild(clone);
      this.animationClones.add(clone);
      return clone;
    }

    applyFlipSurface(items, visibleIds, { animate = true, container = null, featuredMediaId = null } = {}) {
      if (!items.length) return;

      const targetVisibleItems = items.filter((item) => visibleIds.has(this.getProductMediaId(item)));
      const currentVisibleItems = items.filter((item) => this.isNodeVisible(item));
      const currentVisibleIds = new Set(currentVisibleItems.map((item) => this.getProductMediaId(item)));
      const leavingItems = currentVisibleItems.filter((item) => !visibleIds.has(this.getProductMediaId(item)));
      const enteringItems = targetVisibleItems.filter((item) => !currentVisibleIds.has(this.getProductMediaId(item)));
      const shouldAnimate = animate && !this.prefersReducedMotion() && !!container;

      if (!shouldAnimate) {
        this.hydrateNodes(targetVisibleItems);
        items.forEach((item) => this.setNodeVisible(item, visibleIds.has(this.getProductMediaId(item))));
        this.reorderSurface(items, container, { visibleIds, featuredMediaId });
        return;
      }

      const firstRects = new Map(
        currentVisibleItems.map((item) => [item, item.getBoundingClientRect()])
      );
      const leavingClones = leavingItems
        .map((item) => this.createLeavingClone(item))
        .filter(Boolean);

      this.hydrateNodes(enteringItems);
      targetVisibleItems.forEach((item) => this.setNodeVisible(item, true));
      leavingItems.forEach((item) => this.setNodeVisible(item, false));
      this.reorderSurface(items, container, { visibleIds, featuredMediaId });

      const lastRects = new Map(
        targetVisibleItems.map((item) => [item, item.getBoundingClientRect()])
      );

      targetVisibleItems.forEach((item) => {
        const firstRect = firstRects.get(item);
        const lastRect = lastRects.get(item);
        item.style.willChange = 'transform, opacity';
        item.style.transition = `transform ${DURATION}ms ${EASING}, opacity ${DURATION}ms ${EASING}`;

        if (firstRect && lastRect) {
          const deltaX = firstRect.left - lastRect.left;
          const deltaY = firstRect.top - lastRect.top;
          if (deltaX || deltaY) {
            item.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
          }
        } else {
          item.style.opacity = '0';
          item.style.transform = 'scale(0.96)';
        }
      });

      requestAnimationFrame(() => {
        targetVisibleItems.forEach((item) => {
          item.style.transform = '';
          item.style.opacity = '';
        });
        leavingClones.forEach((clone) => {
          clone.style.opacity = '0';
          clone.style.transform = 'scale(0.96)';
        });
      });

      const timer = window.setTimeout(() => {
        targetVisibleItems.forEach((item) => {
          item.style.transition = '';
          item.style.transform = '';
          item.style.opacity = '';
          item.style.willChange = '';
        });
        leavingClones.forEach((clone) => {
          this.animationClones.delete(clone);
          clone.remove();
        });
        this.cleanupTimers.delete(timer);
      }, DURATION + 40);
      this.cleanupTimers.add(timer);
    }

    applyViewerSurface({ visibleIds, activeProductMediaId, featuredMediaId = null, animate = true }) {
      if (!this.viewerItems.length) return;

      const currentActiveItem = this.viewerItems.find(
        (item) => item.classList.contains('is-active') && this.isNodeVisible(item)
      ) || this.viewerItems.find((item) => item.classList.contains('is-active'));
      const nextActiveItem = this.viewerItems.find(
        (item) => this.getProductMediaId(item) === activeProductMediaId && visibleIds.has(activeProductMediaId)
      ) || null;

      const targetVisibleItems = this.viewerItems.filter((item) => visibleIds.has(this.getProductMediaId(item)));
      this.hydrateNodes(targetVisibleItems);

      const shouldAnimate = animate && !this.prefersReducedMotion();
      const leavingClone =
        shouldAnimate && currentActiveItem && currentActiveItem !== nextActiveItem
          ? this.createViewerClone(currentActiveItem)
          : null;

      this.viewerItems.forEach((item) => this.setNodeVisible(item, visibleIds.has(this.getProductMediaId(item))));
      this.reorderSurface(this.viewerItems, this.viewerList, { visibleIds, featuredMediaId });

      if (nextActiveItem) {
        this.viewerItems.forEach((item) => item.classList.toggle('is-active', item === nextActiveItem));
      } else {
        this.viewerItems.forEach((item) => item.classList.remove('is-active'));
      }

      if (!shouldAnimate) return;

      const nextVisual = nextActiveItem?.querySelector('.product__media, .media-wrapper, .media') || null;
      if (nextVisual && currentActiveItem !== nextActiveItem) {
        nextVisual.style.willChange = 'opacity, transform';
        nextVisual.style.opacity = '0';
        nextVisual.style.transform = 'scale(0.985)';
        nextVisual.style.transition = `opacity ${DURATION}ms ${EASING}, transform ${DURATION}ms ${EASING}`;
      }

      requestAnimationFrame(() => {
        if (nextVisual && currentActiveItem !== nextActiveItem) {
          nextVisual.style.opacity = '1';
          nextVisual.style.transform = '';
        }
        if (leavingClone) {
          leavingClone.style.opacity = '0';
          leavingClone.style.transform = 'scale(0.985)';
        }
      });

      const timer = window.setTimeout(() => {
        if (nextVisual) {
          nextVisual.style.transition = '';
          nextVisual.style.opacity = '';
          nextVisual.style.transform = '';
          nextVisual.style.willChange = '';
        }
        if (leavingClone) {
          this.animationClones.delete(leavingClone);
          leavingClone.remove();
        }
        this.cleanupTimers.delete(timer);
      }, DURATION + 40);
      this.cleanupTimers.add(timer);
    }

    createViewerClone(item) {
      const visual = item?.querySelector('.product__media, .media-wrapper, .media') || item;
      const rect = visual?.getBoundingClientRect?.();
      if (!rect || !rect.width || !rect.height) return null;

      const clone = visual.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.classList.add('variant-media-clone', 'variant-media-clone--viewer');
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.style.position = 'fixed';
      clone.style.left = `${rect.left}px`;
      clone.style.top = `${rect.top}px`;
      clone.style.width = `${rect.width}px`;
      clone.style.height = `${rect.height}px`;
      clone.style.margin = '0';
      clone.style.pointerEvents = 'none';
      clone.style.zIndex = '60';
      clone.style.transformOrigin = 'center center';
      clone.style.transition = `opacity ${DURATION}ms ${EASING}, transform ${DURATION}ms ${EASING}`;
      document.body.appendChild(clone);
      this.animationClones.add(clone);
      return clone;
    }

    applyLayeredSurface({ visibleIds, activeProductMediaId, featuredMediaId = null, animate = true }) {
      if (!this.layeredItems.length) return;

      const orderedLayeredItems = this.reorderSurface(this.layeredItems, this.layeredContainer, { visibleIds, featuredMediaId });
      const targetVisibleItems = orderedLayeredItems.filter((item) => visibleIds.has(this.getProductMediaId(item))).slice(0, 4);
      const targetVisibleIds = new Set(targetVisibleItems.map((item) => this.getProductMediaId(item)));
      const enteringItems = targetVisibleItems.filter((item) => !this.isNodeVisible(item));
      this.hydrateNodes(enteringItems);

      const shouldAnimate = animate && !this.prefersReducedMotion();
      this.layeredItems.forEach((item) => {
        const shouldShow = targetVisibleIds.has(this.getProductMediaId(item));
        item.removeAttribute('data-layered-slot');
        if (shouldShow) {
          this.setNodeVisible(item, true);
          if (shouldAnimate && enteringItems.includes(item)) {
            item.style.opacity = '0';
            item.style.transform = 'scale(0.96)';
            item.style.transition = `opacity ${DURATION}ms ${EASING}, transform ${DURATION}ms ${EASING}`;
            requestAnimationFrame(() => {
              item.style.opacity = '';
              item.style.transform = '';
            });
          }
        } else {
          this.setNodeVisible(item, false);
        }
        item.classList.toggle('is-active', this.getProductMediaId(item) === activeProductMediaId && shouldShow);
      });

      targetVisibleItems.forEach((item, index) => {
        item.setAttribute('data-layered-slot', String(index + 1));
      });
    }

    applyModalSurface(visibleIds, animate = false, featuredMediaId = null) {
      if (!this.modalItems.length) return;
      this.applyFlipSurface(this.modalItems, visibleIds, {
        animate,
        container: this.modalContent,
        featuredMediaId,
      });
    }

    syncViewerActive(activeProductMediaId, featuredMediaId = null) {
      if (!activeProductMediaId) {
        this.viewerItems.forEach((item) => item.classList.remove('is-active'));
        this.thumbnailItems.forEach((item) => {
          item.classList.remove('is-active');
          item.querySelector('button')?.removeAttribute('aria-current');
        });
        return;
      }

      const nextViewerItem = this.viewerItems.find(
        (item) => this.getProductMediaId(item) === activeProductMediaId && this.isNodeVisible(item)
      );
      const resolvedFeaturedMediaId = String(featuredMediaId || '').trim();
      const shouldPinActiveFirst = !!resolvedFeaturedMediaId && activeProductMediaId === resolvedFeaturedMediaId;

      if (this.mediaGallery && nextViewerItem) {
        this.mediaGallery.setActiveMedia(nextViewerItem.dataset.mediaId, shouldPinActiveFirst, false);
        return;
      }

      if (nextViewerItem) {
        if (shouldPinActiveFirst && this.viewerList && nextViewerItem.parentElement === this.viewerList) {
          this.viewerList.prepend(nextViewerItem);
        }
        this.viewerItems.forEach((item) => item.classList.toggle('is-active', item === nextViewerItem));
        if (this.viewerList) {
          this.viewerList.scrollTo({ left: nextViewerItem.offsetLeft });
        }
      }
    }

    syncStackActive(activeProductMediaId) {
      if (!this.stackItems.length) return;
      this.stackItems.forEach((item) => {
        const shouldBeActive = !!activeProductMediaId && this.getProductMediaId(item) === activeProductMediaId && this.isNodeVisible(item);
        item.classList.toggle('is-active', shouldBeActive);
      });
    }

    syncModalActive({ visibleIds, activeProductMediaId }) {
      if (!this.modalItems.length || !this.productModal) return;

      const currentActiveItem = this.modalItems.find(
        (item) => item.classList.contains('is-active') && this.isNodeVisible(item)
      ) || this.modalItems.find((item) => item.classList.contains('is-active'));

      if (this.isModalOpen()) {
        if (currentActiveItem && visibleIds.has(this.getProductMediaId(currentActiveItem))) {
          return;
        }

        if (activeProductMediaId && visibleIds.has(activeProductMediaId)) {
          this.productModal.setActiveMediaById(activeProductMediaId, false);
        } else {
          this.productModal.hide();
        }
        return;
      }

      this.modalItems.forEach((item) => {
        const shouldBeActive = !!activeProductMediaId && this.getProductMediaId(item) === activeProductMediaId && this.isNodeVisible(item);
        item.classList.toggle('is-active', shouldBeActive);
      });
    }

    refreshInteractiveComponents() {
      if (this.productGallery) {
        this.productGallery.resetPages?.();
        this.productGallery.update?.();
      }
      if (this.thumbnailSlider) {
        this.thumbnailSlider.resetPages?.();
        this.thumbnailSlider.update?.();
      }
      if (this.stackContainer?.refresh) {
        this.stackContainer.refresh();
      }
    }
  }

  const registry = new WeakMap();

  window.VariantMediaController = VariantMediaController;
  window.VariantMediaControllerRegistry = {
    getOrCreate(root) {
      if (!root) return null;
      if (!registry.has(root)) {
        registry.set(root, new VariantMediaController(root));
      }
      return registry.get(root);
    },
    destroy(root) {
      const controller = registry.get(root);
      if (!controller) return;
      controller.destroy();
      registry.delete(root);
    },
  };
})();
